import type { SupabaseClient } from '@supabase/supabase-js'
import type { ActivityItem, AnalyticsEvent, Chain, Draft, Echo, FeedCursor, Loop, MediaAsset, Profile, RemixEdge, RenderJob, RenderRecipe, Report, Scene, UploadSession } from './types'
import { db, saveLocalMedia } from './db'
import { feedCursorSchema, renderRecipeSchema, uploadSessionSchema } from './contracts'

export interface UploadProgress { loaded: number; total: number }
export interface UploadResult { mediaUrl: string; posterUrl?: string }
export interface VideoStorageProvider {
  mode: 'demo' | 'supabase'
  upload(file: Blob, options?: { signal?: AbortSignal; onProgress?: (progress: UploadProgress) => void }): Promise<UploadResult>
  remove(mediaUrl: string): Promise<void>
}
export interface VideoPipelineProvider extends VideoStorageProvider {
  requestUpload(input: { fileName: string; fileType: string; fileSize: number; durationMs?: number; signal?: AbortSignal }): Promise<UploadSession>
  uploadSource(session: UploadSession, file: Blob, options?: { signal?: AbortSignal; onProgress?: (progress: UploadProgress) => void }): Promise<MediaAsset>
  submitRender(input: { draftId: string; recipe: RenderRecipe }): Promise<RenderJob>
  getRenderJob(id: string): Promise<RenderJob>
  retryRender(id: string): Promise<RenderJob>
  cleanup(assetId: string): Promise<void>
}
export interface AuthProvider { currentUser(): Promise<Profile | null>; signOut(): Promise<void> }
export interface LoopRepository { feed(kind: 'for-you' | 'following' | 'fresh', cursor?: FeedCursor): Promise<{ loops: Loop[]; next?: FeedCursor }>; byId(id: string): Promise<Loop | null>; byIds(ids: string[]): Promise<Loop[]>; byCreator(profileId: string): Promise<Loop[]>; replies(parentLoopId: string): Promise<Loop[]>; byScene(sceneId: string): Promise<Loop[]>; byEcho(echoId: string): Promise<Loop[]>; saved(): Promise<Loop[]>; publish(draft: Draft, job?: RenderJob): Promise<Loop> }
export interface SocialRepository { profile(handle: string): Promise<Profile | null>; profilesByIds(ids: string[]): Promise<Profile[]>; toggleLike(loopId: string): Promise<boolean>; toggleSave(loopId: string): Promise<boolean>; follow(profileId: string): Promise<void>; unfollow(profileId: string): Promise<void>; hide(loopId: string, reason: 'hide' | 'not-interested'): Promise<void> }
export interface DiscoveryRepository { scenes(): Promise<Scene[]>; echoes(): Promise<Echo[]>; chain(id: string): Promise<Chain | null>; chainsByOwner(profileId: string): Promise<Chain[]>; sceneVelocity(sceneId: string): Promise<{ velocity: number; acceleration: number; uniqueCreators: number; forkCompletions: number; videoReplies: number; windowMinutes: number }> }
export interface NotificationRepository { list(): Promise<ActivityItem[]>; markRead(id: string): Promise<void>; subscribe(userId: string, onChange: () => void): () => void }
export interface ModerationRepository { queue(): Promise<Report[]>; act(reportId: string, action: 'no_violation' | 'label_sensitive' | 'remove', reason?: string): Promise<void>; report(input: { loopId?: string; accountId?: string; reason: string; details?: string }): Promise<void>; block(profileId: string): Promise<void>; mute(profileId: string): Promise<void> }
export interface RemixRepository { createFork(sourceLoopId: string, childLoopId: string, recipe: RenderRecipe): Promise<RemixEdge>; lineage(loopId: string): Promise<RemixEdge[]> }
export interface SocialGraphProvider { follow(profileId: string): Promise<void>; unfollow(profileId: string): Promise<void> }
export interface PlatformAuthProvider { connect(): Promise<{ connected: boolean }> }
export interface PlatformPublisher { publishLoop(loop: Loop, text?: string): Promise<{ externalId: string }> }
export interface PlatformTrendProvider { trends(): Promise<{ name: string; velocity: number; suggestedScene?: Scene }[]> }
export interface AnalyticsProvider { track(event: AnalyticsEvent): Promise<void> }
export interface DirectorProvider { analyze(draft: Draft): Promise<{ title: string; detail: string; confidence: number }[]> }

export const demoVideoStorage: VideoStorageProvider = {
  mode: 'demo',
  async upload(file) {
    const id = await saveLocalMedia(file, 'demo-upload')
    return { mediaUrl: `idb://${id}` }
  },
  async remove(mediaUrl) {
    if (mediaUrl.startsWith('idb://')) await db.media.delete(mediaUrl.slice(6))
  }
}

export const demoVideoPipeline: VideoPipelineProvider = {
  ...demoVideoStorage,
  async requestUpload(input) {
    const session = uploadSessionSchema.parse({ id: crypto.randomUUID(), assetId: crypto.randomUUID(), path: `local/${crypto.randomUUID()}-${input.fileName}`, uploadUrl: 'https://local.invalid/device-only', expiresAt: new Date(Date.now() + 30 * 60_000).toISOString(), maxBytes: Math.max(input.fileSize, 1) })
    await db.uploadSessions.put(session)
    return session
  },
  async uploadSource(session, file, options) {
    if (file.size > session.maxBytes) throw new Error('The file is larger than the reserved upload.')
    if (options?.signal?.aborted) throw new DOMException('Upload cancelled.', 'AbortError')
    const mediaId = await saveLocalMedia(file, session.path)
    options?.onProgress?.({ loaded: file.size, total: file.size })
    const now = new Date().toISOString()
    const asset: MediaAsset = { id: session.assetId, ownerId: 'p-you', status: 'uploaded', sourceUrl: `idb://${mediaId}`, mimeType: file.type, createdAt: now, updatedAt: now }
    await db.assets.put(asset)
    return asset
  },
  async submitRender(input) {
    const recipe = renderRecipeSchema.parse(input.recipe)
    const now = new Date().toISOString()
    const job: RenderJob = { id: crypto.randomUUID(), ownerId: 'p-you', draftId: input.draftId, status: 'ready', progress: 100, recipe, attempts: 1, createdAt: now, updatedAt: now }
    await db.renderJobs.put(job)
    return job
  },
  async getRenderJob(id) {
    const job = await db.renderJobs.get(id)
    if (!job) throw new Error('Render job not found on this device.')
    return job
  },
  async retryRender(id) {
    const job = await this.getRenderJob(id)
    const next = { ...job, status: 'ready' as const, progress: 100, attempts: job.attempts + 1, updatedAt: new Date().toISOString(), error: undefined }
    await db.renderJobs.put(next)
    return next
  },
  async cleanup(assetId) {
    const asset = await db.assets.get(assetId)
    if (asset?.sourceUrl?.startsWith('idb://')) await db.media.delete(asset.sourceUrl.slice(6))
    await db.assets.delete(assetId)
  }
}

export const localAnalytics: AnalyticsProvider = {
  async track(event) {
    await db.events.add({ name: event.name, at: event.at, payload: event as unknown as Record<string, unknown> })
    if (import.meta.env.VITE_DATA_MODE !== 'supabase' || !event.loopId) return
    try {
      const client = await getSupabaseClient(); if (!client) return
      const { data: { user } } = await client.auth.getUser(); if (!user) return
      const suppliedSession = event.sessionId
      const sessionId = /^[0-9a-f-]{36}$/i.test(suppliedSession) ? suppliedSession : (sessionStorage.getItem('loop6-beta-session') ?? crypto.randomUUID())
      sessionStorage.setItem('loop6-beta-session', sessionId)
      if (event.name === 'loop_completed' || event.name === 'loop_rewatched') {
        await client.rpc('record_loop_completion', { target_loop_id: event.loopId, target_session_id: sessionId, target_cycle: Number(event.metadata?.depth ?? 1) })
        return
      }
      const eventType = event.name === 'loop_started' ? 'started' : event.name === 'loop_shared' ? 'shared' : event.name === 'fork_started' ? 'fork_started' : event.name === 'fork_published' ? 'fork_completed' : event.name === 'video_reply_published' ? 'reply_created' : null
      if (eventType) await client.from('view_events').insert({ loop_id: event.loopId, viewer_id: user.id, session_id: sessionId, event_type: eventType, metadata: event.metadata ?? {} })
    } catch { /* Analytics never interrupts creation or playback. */ }
  }
}

export const localDirector: DirectorProvider = {
  async analyze(draft) {
    const duration = draft.trimEndMs - draft.trimStartMs
    return [
      { title: duration > 5800 ? 'Tighten the exit' : 'Timing is compact', detail: duration > 5800 ? 'Try ending 180 ms earlier; the final beat will land harder.' : 'The current cut leaves room for the loop to breathe.', confidence: 0.84 },
      { title: 'Seam candidate', detail: 'The first and final frames share a similar center of motion.', confidence: 0.76 },
      { title: 'Alt text starter', detail: draft.caption ? `A six-second scene: ${draft.caption}` : 'Describe the action and the final transformation.', confidence: 0.92 }
    ]
  }
}

let sharedSupabaseClient: SupabaseClient | null | undefined
export async function getSupabaseClient(): Promise<SupabaseClient | null> {
  if (sharedSupabaseClient !== undefined) return sharedSupabaseClient
  const url = import.meta.env.VITE_SUPABASE_URL
  const key = import.meta.env.VITE_SUPABASE_ANON_KEY
  if (!url || !key) { sharedSupabaseClient = null; return null }
  const { createClient } = await import('@supabase/supabase-js')
  sharedSupabaseClient = createClient(url, key, { auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true } })
  return sharedSupabaseClient
}

export async function createSupabaseVideoPipelineProvider(): Promise<VideoPipelineProvider | null> {
  const client = await getSupabaseClient()
  const url = import.meta.env.VITE_SUPABASE_URL
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
  if (!client || !url || !anonKey) return null
  const directMode = import.meta.env.VITE_MEDIA_PIPELINE_MODE === 'direct'
  const directJobs = new Map<string, RenderJob>()
  const mapJob = (row: Record<string, unknown>): RenderJob => ({
    id: String(row.id), ownerId: String(row.owner_id), draftId: String(row.draft_id ?? ''), status: row.status as RenderJob['status'], progress: Number(row.progress), recipe: renderRecipeSchema.parse(row.recipe), outputAssetId: row.output_asset_id ? String(row.output_asset_id) : undefined, attempts: Number(row.attempts), error: row.error_detail ? String(row.error_detail) : undefined, createdAt: String(row.created_at), updatedAt: String(row.updated_at)
  })
  const provider: VideoPipelineProvider = {
    mode: 'supabase',
    async upload(file, options) {
      const uploadSession = await provider.requestUpload({ fileName: 'source-video', fileType: file.type, fileSize: file.size })
      const asset = await provider.uploadSource(uploadSession, file, options)
      return { mediaUrl: asset.sourceUrl ?? `asset://${asset.id}`, posterUrl: asset.posterUrl }
    },
    async remove(mediaUrl) {
      if (!mediaUrl.startsWith('asset://')) throw new Error('Only provider-owned asset references can be removed.')
      await provider.cleanup(mediaUrl.slice(8))
    },
    async requestUpload(input) {
      if (input.signal?.aborted) throw new DOMException('Upload cancelled.', 'AbortError')
      if (directMode && !input.durationMs) throw new Error('The free beta needs a readable video duration before upload.')
      const response = directMode
        ? await client.rpc('request_direct_upload', { file_name: input.fileName, mime_type: input.fileType, byte_size: input.fileSize, source_duration_ms: Math.round(input.durationMs!) })
        : await client.functions.invoke('request-upload', { body: { filename: input.fileName, mimeType: input.fileType, bytes: input.fileSize, durationMs: input.durationMs } })
      const { data, error } = response
      if (error) throw error
      return uploadSessionSchema.parse(directMode ? { ...data as Record<string, unknown>, uploadUrl: `${url}/storage/v1/upload/resumable` } : data)
    },
    async uploadSource(uploadSession, file, options) {
      const { data: { session } } = await client.auth.getSession(); if (!session) throw new Error('Sign in before uploading across devices.')
      const { Upload } = await import('tus-js-client')
      await new Promise<void>((resolve, reject) => {
        const upload = new Upload(file, { endpoint: uploadSession.uploadUrl, retryDelays: [0, 1000, 3000, 5000], chunkSize: 6 * 1024 * 1024, headers: { Authorization: `Bearer ${session.access_token}`, apikey: anonKey }, metadata: { bucketName: directMode ? 'loop-media' : 'loop-raw', objectName: uploadSession.path, contentType: file.type.split(';')[0] || 'application/octet-stream', cacheControl: directMode ? '31536000' : '3600' }, removeFingerprintOnSuccess: true, onProgress: (loaded, total) => options?.onProgress?.({ loaded, total }), onError: reject, onSuccess: () => resolve() })
        options?.signal?.addEventListener('abort', () => { upload.abort(true).catch(() => undefined); reject(new DOMException('Upload cancelled.', 'AbortError')) }, { once: true })
        upload.findPreviousUploads().then((previous) => { if (previous[0]) upload.resumeFromPreviousUpload(previous[0]); upload.start() }).catch(reject)
      })
      const completedAt = new Date().toISOString()
      if (directMode) {
        const publicUrl = client.storage.from('loop-media').getPublicUrl(uploadSession.path).data.publicUrl
        return { id: uploadSession.assetId, ownerId: session.user.id, status: 'uploaded', sourceUrl: publicUrl, mimeType: file.type, createdAt: completedAt, updatedAt: completedAt }
      }
      const { error: completeError } = await client.rpc('complete_upload', { target_session_id: uploadSession.id }); if (completeError) throw completeError
      const { data: signed, error: signedError } = await client.storage.from('loop-raw').createSignedUrl(uploadSession.path, 3600); if (signedError) throw signedError
      return { id: uploadSession.assetId, ownerId: session.user.id, status: 'uploaded', sourceUrl: signed.signedUrl, mimeType: file.type, createdAt: completedAt, updatedAt: completedAt }
    },
    async submitRender(input) {
      const recipe = renderRecipeSchema.parse(input.recipe)
      if (directMode) {
        const segment = recipe.segments[0]
        const sourceDuration = segment?.sourceDurationMs ?? segment?.outMs ?? 0
        const unchanged = recipe.segments.length === 1 && segment?.mediaAssetId && recipe.loopMode === 'hard' && recipe.textCues.length === 0 && recipe.originalAudio && segment.filter === 'none' && segment.playbackRate === 1 && segment.volume === 1 && segment.inMs <= 25 && Math.abs(segment.outMs - sourceDuration) <= 50
        if (!unchanged || !segment?.mediaAssetId) throw new Error('Free beta publishing currently accepts one ready 0.5–6.0 second file with a Hard Loop. Your draft is safe; server-rendered cuts and effects arrive with the paid media upgrade.')
        const durationMs = Math.round(segment.outMs - segment.inMs)
        if (durationMs < 500 || durationMs > 6000) throw new Error('A published Loop must be between 0.5 and 6.0 seconds.')
        const finalized = await client.rpc('finalize_direct_upload', { target_asset_id: segment.mediaAssetId }); if (finalized.error) throw finalized.error
        const { data: { user } } = await client.auth.getUser(); if (!user) throw new Error('Sign in before publishing.')
        const now = new Date().toISOString(); const id = crypto.randomUUID()
        const job: RenderJob = { id, ownerId: user.id, draftId: input.draftId, status: 'ready', progress: 100, recipe, outputAssetId: String(finalized.data), attempts: 1, createdAt: now, updatedAt: now }
        directJobs.set(id, job)
        return job
      }
      const { data, error } = await client.functions.invoke('submit-render', { body: { draftId: input.draftId, recipe } })
      if (error) throw error
      return provider.getRenderJob(String(data.id))
    },
    async getRenderJob(id) {
      if (directMode) { const job = directJobs.get(id); if (!job) throw new Error('Direct publish job is no longer available. Your uploaded media is still safe.'); return job }
      const { data, error } = await client.from('render_jobs').select('*').eq('id', id).single(); if (error) throw error
      return mapJob(data as Record<string, unknown>)
    },
    async retryRender(id) {
      if (directMode) return provider.getRenderJob(id)
      const { error } = await client.rpc('retry_render_job', { target_job_id: id }); if (error) throw error
      return provider.getRenderJob(id)
    },
    async cleanup(assetId) {
      const { data: asset, error } = await client.from('media_assets').select('storage_bucket,storage_path').eq('id', assetId).single(); if (error) throw error
      if (asset.storage_bucket && asset.storage_path) { const result = await client.storage.from(asset.storage_bucket).remove([asset.storage_path]); if (result.error) throw result.error }
      const deleted = await client.rpc('delete_unused_asset', { target_asset_id: assetId }); if (deleted.error) throw deleted.error
    }
  }
  return provider
}

export async function getVideoPipelineProvider(): Promise<VideoPipelineProvider> {
  if (import.meta.env.VITE_DATA_MODE === 'supabase') {
    const provider = await createSupabaseVideoPipelineProvider()
    if (!provider) throw new Error('The beta media pipeline is not configured. Demo fallback is disabled in server mode.')
    return provider
  }
  return demoVideoPipeline
}

export const platformMocks = {
  auth: { async connect() { return { connected: true } } } satisfies PlatformAuthProvider,
  publisher: { async publishLoop(loop: Loop) { return { externalId: `host-${loop.id}` } } } satisfies PlatformPublisher,
  trends: { async trends() { return [{ name: 'THE FINAL EVERYONE SAW', velocity: 94 }, { name: 'TUESDAY WEATHER', velocity: 71 }, { name: 'NIGHT TRAIN', velocity: 63 }] } } satisfies PlatformTrendProvider
}

export function encodeFeedCursor(cursor: FeedCursor): string {
  return btoa(JSON.stringify(feedCursorSchema.parse(cursor)))
}

export function decodeFeedCursor(value: string): FeedCursor {
  return feedCursorSchema.parse(JSON.parse(atob(value)) as unknown)
}
